import { FORM_ERROR } from "final-form";
import arrayMutators from "final-form-arrays";
import createDecorator from "final-form-focus";
import { every, isEqual } from "lodash";
import { FC, useState } from "react";
import { Form, FormSpy } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { useDispatch, useSelector } from "react-redux";

import { Add } from "@livechat/design-system-icons";
import { Button, Icon, SpacingToken } from "@livechat/design-system-react-components";

import { IAgentFormRow } from "components/agents-limit-reached";
import { ButtonGroup } from "components/button/ButtonGroup";
import Block from "components/layout/Block";
import { StyledModalFooterBar } from "components/modal";
import { InviteButton } from "components/payment-failures-limits/InviteButton";
import { PaymentFailedAlert, PaymentFailedLocation } from "components/payment-failures-limits/PaymentFailedAlert";
import Role from "constants/role";
import { willUpgradeSubscription } from "helpers/subscription";
import { usePaymentFailure } from "hooks/use-payment-failure";
import { getNonViewersCount, selectIsAdmin } from "store/entities/agents";
import { getBilling } from "store/entities/billing";
import { selectIsManagedByShopify, selectIsShopifyTrial } from "store/entities/integrations";
import { selectDefaultTeamId, selectManagedByPartner } from "store/entities/license";
import { selectCurrentSubscription, selectSeatsCount } from "store/entities/subscriptions";
import { selectTeamsAlphabetically } from "store/entities/teams";
import { AgentsViewActions, getIsSavingAgents } from "store/views/agents";
import { withVar } from "themes/helpers";
import { getNonViewersToAdd, normalizeAgentBatchPayload } from "utils/agent";

import { SeatsExceededMessage } from "../../../_components/payment-failures-limits/SeatsExceededMessage";

import { AgentFieldRow } from "./AgentFieldRow";
import { Body, StyledForm } from "./styled";

const focusOnError = createDecorator<IAgentFormData>();

export interface IAgentFormData {
  agents: IAgentFormRow[];
}

interface IProps {
  onModalClose(): void;
  onConfirmBeforeExitingChange(confirmBeforeExiting: boolean): void;
}

export const AddAgentsModalContent: FC<IProps> = ({ onModalClose, onConfirmBeforeExitingChange }) => {
  const dispatch = useDispatch();
  const subscription = useSelector(selectCurrentSubscription);
  const teams = useSelector(selectTeamsAlphabetically);
  const defaultTeamId = useSelector(selectDefaultTeamId);
  const seatsCount = useSelector(selectSeatsCount);
  const nonViewersCount = useSelector(getNonViewersCount);
  const isAdmin = useSelector(selectIsAdmin);
  const isManagedByPartner = useSelector(selectManagedByPartner);
  const isManagedByShopify = useSelector(selectIsManagedByShopify);
  const isShopifyTrial = useSelector(selectIsShopifyTrial);
  const billing = useSelector(getBilling);
  const isSaving = useSelector(getIsSavingAgents);
  const { failed, reset } = usePaymentFailure();

  const [nonViewersToAdd, setNonViewersToAdd] = useState(0);

  const isManaged = isManagedByPartner || isManagedByShopify;
  const limitExceededBy = nonViewersToAdd - Math.max((seatsCount ?? nonViewersCount) - nonViewersCount, 0);

  const willUpgrade = willUpgradeSubscription(subscription, billing, nonViewersCount, nonViewersToAdd, isManaged);

  const handleInvitedCountChange = (agents: IAgentFormRow[]): void => {
    const newNonViewersToAdd = getNonViewersToAdd(agents);
    if (nonViewersToAdd > newNonViewersToAdd) reset();
    setNonViewersToAdd(newNonViewersToAdd);
  };

  const agentRow: IAgentFormRow = {
    permission: isAdmin ? Role.Owner : Role.Normal,
    teams: [defaultTeamId],
    email: "",
  };
  const initialValues: IAgentFormData = {
    agents: [agentRow, agentRow, agentRow],
  };
  const hasTeams = teams.length > 1;

  const handleSaveAgents = (formData: IAgentFormData): void => {
    const normalizedAgents = normalizeAgentBatchPayload(formData.agents);
    if (normalizedAgents.length > 0) {
      dispatch(AgentsViewActions.createAgentsBatchRequest({ agents: normalizedAgents }));
    }
  };

  const validate = (values: IAgentFormData): Record<string, string> =>
    values.agents.some(agent => agent.email) ? {} : { [FORM_ERROR]: "At least one agent must be invited" };

  return (
    <Form
      key="add-multiple-agents-form"
      decorators={[focusOnError]}
      mutators={{
        ...arrayMutators,
      }}
      initialValues={initialValues}
      keepDirtyOnReinitialize
      onSubmit={handleSaveAgents}
      validate={validate}
      subscription={{ pristine: true, errors: true }}
      render={({ handleSubmit, errors }) => (
        <StyledForm onSubmit={handleSubmit}>
          <FormSpy
            onChange={({ active, pristine, values }) => {
              if (!active && !pristine) {
                onConfirmBeforeExitingChange(!failed && !every(values.agents, agent => isEqual(agent, agentRow)));
                handleInvitedCountChange(values.agents);
              }
            }}
          />
          <FieldArray<IAgentFormRow>
            name="agents"
            subscription={{ pristine: true }}
            render={({ fields }) => (
              <Body>
                <Block>
                  {fields.map((key: string, index: number) => (
                    <AgentFieldRow
                      key={key}
                      teams={teams}
                      index={index}
                      fieldName={key}
                      fields={fields}
                      hasTeams={hasTeams}
                    />
                  ))}
                </Block>
                <Block marginBottom={withVar(SpacingToken.Spacing4)}>
                  <Button
                    disabled={false}
                    onClick={() => fields.push(agentRow)}
                    kind="link"
                    size="xcompact"
                    icon={<Icon source={Add} size="small" />}
                  >
                    Invite Agents
                  </Button>
                </Block>
                {willUpgrade && limitExceededBy > 0 && !isShopifyTrial && (
                  <SeatsExceededMessage limitExceededBy={limitExceededBy} />
                )}
                {failed && (
                  <Block paddingTop={withVar(SpacingToken.Spacing4)}>
                    <PaymentFailedAlert location={PaymentFailedLocation.Invite} />
                  </Block>
                )}
              </Body>
            )}
          />
          <StyledModalFooterBar>
            <ButtonGroup>
              <Button data-testid="add-agent-modal-close-button" onClick={onModalClose}>
                Close
              </Button>
              <InviteButton
                disabled={!!errors?.[FORM_ERROR]}
                isSaving={isSaving}
                failed={failed}
                limitExceeded={limitExceededBy > 0}
              />
            </ButtonGroup>
          </StyledModalFooterBar>
        </StyledForm>
      )}
    />
  );
};
